import { apiSlice } from '../../api';
import type {
  CancelAmlTransactionDTO,
  CancelAmlTransactionResult,
  CancelTransactionDTO,
  CancelTransactionResult,
  ConfirmAmlTransactionDTO,
  ConfirmAmlTransactionResult,
  DeleteTransactionDocumentByIdDTO,
  DeleteTransactionResult,
  ExecuteTransactionDTO,
  ExecuteTransactionResult,
  GetAmlCheckDTO,
  GetAmlCheckResult,
  GetProfileTransactionsDTO,
  GetProfileTransactionsResult,
  GetTransactionDocumentsDTO,
  GetTransactionDocumentsResult,
  GetTransactionDTO,
  GetTransactionExchangeOrderDTO,
  GetTransactionExchangeOrderResult,
  GetTransactionResult,
  GetTransactionsDTO,
  GetTransactionsResult,
  PostTransactionDocumentResult,
  PostTransactionDocumentsDTO,
} from './types';

export const adminTransactionsEndpoints = apiSlice.injectEndpoints({
  endpoints: (build) => {
    return {
      getTransactions: build.query<GetTransactionsResult, GetTransactionsDTO>({
        query: ({ params }) => {
          return {
            url: '/admin/v1/transactions',
            method: 'GET',
            params,
          };
        },
        providesTags: ['Transactions'],
      }),

      getProfileTransactions: build.query<GetProfileTransactionsResult, GetProfileTransactionsDTO>({
        query: ({ profileUid, params }) => {
          return {
            url: `/admin/v1/profiles/${profileUid}/transactions`,
            method: 'GET',
            params,
          };
        },
        providesTags: ['ProfileTransactions'],
      }),

      getTransaction: build.query<GetTransactionResult, GetTransactionDTO>({
        query: ({ transactionId }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}`,
            method: 'GET',
          };
        },
        providesTags: ['Transaction'],
      }),

      getTransactionDocuments: build.query<
        GetTransactionDocumentsResult,
        GetTransactionDocumentsDTO
      >({
        query: ({ transactionId }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/documents`,
            method: 'GET',
          };
        },
        providesTags: ['Transaction'],
      }),

      postTransactionDocument: build.mutation<
        PostTransactionDocumentResult,
        PostTransactionDocumentsDTO
      >({
        query: ({ transactionId, body }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/documents`,
            method: 'POST',
            body,
          };
        },
      }),

      deleteTransactionDocumentById: build.mutation<
        DeleteTransactionResult,
        DeleteTransactionDocumentByIdDTO
      >({
        query: ({ transactionId, documentId }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/documents/${documentId}`,
            method: 'DELETE',
          };
        },
      }),

      getTransactionExchangeOrder: build.query<
        GetTransactionExchangeOrderResult,
        GetTransactionExchangeOrderDTO
      >({
        query: ({ transactionId }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/exchange-order`,
            method: 'GET',
          };
        },
      }),

      getAmlCheck: build.query<GetAmlCheckResult, GetAmlCheckDTO>({
        query: (params) => {
          return {
            url: '/admin/v1/aml/checks',
            method: 'GET',
            params,
          };
        },
      }),

      executeTransaction: build.mutation<ExecuteTransactionResult, ExecuteTransactionDTO>({
        query: ({ transactionId, body }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/execute`,
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['Transaction', 'Transactions', 'ProfileTransactions'],
      }),

      cancelTransaction: build.mutation<CancelTransactionResult, CancelTransactionDTO>({
        query: ({ transactionId, body }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/cancel`,
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['Transaction', 'Transactions', 'ProfileTransactions'],
      }),

      confirmAmlTransaction: build.mutation<ConfirmAmlTransactionResult, ConfirmAmlTransactionDTO>({
        query: ({ transactionId, password, body }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/aml/confirm`,
            method: 'POST',
            headers: {
              ['confirm-action-code']: password,
            },
            body,
          };
        },
        invalidatesTags: ['Transaction', 'Transactions', 'ProfileTransactions'],
      }),

      cancelAmlTransaction: build.mutation<CancelAmlTransactionResult, CancelAmlTransactionDTO>({
        query: ({ transactionId, body, password }) => {
          return {
            url: `/admin/v1/transactions/${transactionId}/aml/cancel`,
            method: 'POST',
            headers: {
              ['confirm-action-code']: password,
            },
            body,
          };
        },
        invalidatesTags: ['Transaction', 'Transactions', 'ProfileTransactions'],
      }),
    };
  },
});

export const {
  useLazyGetTransactionsQuery,
  useLazyGetProfileTransactionsQuery,
  useGetTransactionQuery,
  useLazyGetTransactionQuery,
  useGetTransactionExchangeOrderQuery,
  useLazyGetTransactionExchangeOrderQuery,
  useExecuteTransactionMutation,
  useCancelTransactionMutation,
  useGetAmlCheckQuery,
  useLazyGetAmlCheckQuery,
  useConfirmAmlTransactionMutation,
  useCancelAmlTransactionMutation,
  useGetProfileTransactionsQuery,
  useGetTransactionDocumentsQuery,
  useLazyGetTransactionDocumentsQuery,
  usePostTransactionDocumentMutation,
  useDeleteTransactionDocumentByIdMutation,
} = adminTransactionsEndpoints;

export type AdminTransactionsController = typeof adminTransactionsEndpoints;
